import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import Auth from './Auth';
import './App.css';

const CATEGORIES = [
  'LR', 'BR1', 'BR2', 'BR3', 'PRCH', 'CO', 'CB1', 'CB2', 'HB',
  ...Array.from({ length: 40 }, (_, i) => `BX${String(i + 1).padStart(2, '0')}`)
];

const EditableName = ({ value, onUpdate, isReady }) => {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onUpdate(localValue);
    }
  };

  return (
    <textarea
      className={`editable-name-textarea ${isReady ? 'item-ready-text' : ''}`}
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
    />
  );
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [encryptionKey, setEncryptionKey] = useState(sessionStorage.getItem('encryptionKey'));
  const [items, setItems] = useState([]);
  const [newItemName, setNewItemName] = useState('');
  const [newItemCategory, setNewItemCategory] = useState(CATEGORIES[0]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filter, setFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('encryptionKey');
    setToken(null);
    setEncryptionKey(null);
  };

  // Configure axios to send token with every request
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get('/api/list');
        // Decrypt items
        const decryptedItems = response.data.map(item => {
          try {
            const bytes = CryptoJS.AES.decrypt(item.name, encryptionKey);
            const originalText = bytes.toString(CryptoJS.enc.Utf8);
            return { ...item, name: originalText || item.name }; // Fallback to raw if fail
          } catch (e) {
            return item;
          }
        });
        setItems(decryptedItems);
      } catch (error) {
        console.error('Error fetching items:', error);
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          handleLogout();
        }
      }
    };

    if (token && encryptionKey) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchItems();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setItems([]);
    }
  }, [token, encryptionKey]);

  const handleLogin = (newToken, newKey) => {
    sessionStorage.setItem('encryptionKey', newKey);
    setEncryptionKey(newKey);
    setToken(newToken);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) return;

    try {
      const encryptedName = CryptoJS.AES.encrypt(newItemName, encryptionKey).toString();
      const response = await axios.post('/api/list', {
        name: encryptedName,
        category: newItemCategory
      });
      
      // Decrypt the response immediately for local state (or just use local value)
      const savedItem = response.data;
      setItems([...items, { ...savedItem, name: newItemName }]);
      setNewItemName('');
      setNewItemCategory(CATEGORIES[0]);
    } catch (error) {
      console.error('Error adding item:', error);
    }
  };

  const handleUpdateItem = async (id, field, value) => {
    try {
      let payloadValue = value;
      
      if (field === 'name') {
          payloadValue = CryptoJS.AES.encrypt(value, encryptionKey).toString();
      }
      
      await axios.put(`/api/list/${id}`, {
        [field]: payloadValue
      });
      
      setItems(items.map(item => item._id === id ? { ...item, [field]: value } : item));
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      await axios.post('/api/list/delete', {
        ids: Array.from(selectedIds)
      });
      setItems(items.filter(item => !selectedIds.has(item._id)));
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting items:', error);
    }
  };

  const filteredItems = items.filter(item => {
    // Ready Filter
    if (filter === 'READY' && !item.ready) return false;
    if (filter === 'NOT_READY' && item.ready) return false;
    
    // Category Filter
    if (categoryFilter !== 'ALL' && item.category !== categoryFilter) return false;

    return true;
  });

  if (!token) {
    return <Auth onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Takerlist</h1>
        <div style={{ position: 'absolute', right: '20px', top: '20px' }}>
            <button onClick={handleLogout} className="btn-delete">
            Logout
            </button>
        </div>
      </header>

      <main className="container">
        {/* Add Item Form */}
        <section className="add-item-section">
          <form onSubmit={handleAddItem} className="add-form">
            <textarea
              placeholder="Item Name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              className="input-name"
            />
            <select
              value={newItemCategory}
              onChange={(e) => setNewItemCategory(e.target.value)}
              className="select-category"
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button type="submit" className="btn-add">Add</button>
          </form>
        </section>

        {/* Action Bar */}
        <section className="actions-section">
          <div className="filters">
            <div className="filter-group">
              <label>Status: </label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className="filter-select">
                <option value="ALL">All</option>
                <option value="READY">Ready Only</option>
                <option value="NOT_READY">Not Ready</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Category: </label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="filter-select">
                <option value="ALL">All Categories</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <button 
            onClick={handleDeleteSelected} 
            disabled={selectedIds.size === 0}
            className="btn-delete"
          >
            Delete Selected ({selectedIds.size})
          </button>
        </section>

        {/* List */}
        <section className="list-section">
          <table className="items-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Name</th>
                <th>Category</th>
                <th>Ready?</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item._id} className={item.ready ? 'item-ready' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item._id)}
                      onChange={() => toggleSelect(item._id)}
                    />
                  </td>
                  <td className="item-name-cell">
                    <EditableName 
                        value={item.name} 
                        onUpdate={(val) => handleUpdateItem(item._id, 'name', val)}
                        isReady={item.ready}
                    />
                  </td>
                  <td>
                    <select
                      value={item.category}
                      onChange={(e) => handleUpdateItem(item._id, 'category', e.target.value)}
                      className="table-select"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={item.ready}
                      onChange={(e) => handleUpdateItem(item._id, 'ready', e.target.checked)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}

export default App;